import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import * as moment from 'moment';
import { PermissionsService } from '../../common/services/permissions.service';
import { Job, JobStatus } from '../jobs.models';
import { NotifyService } from '../../common/services/notify.service';
import { ConfirmationDialogService } from '../../common/services/confirmation-dialog.service';
import { AppState } from '../../store';
import { Creator } from '../../clusters/clusters.models';
import { ItemType } from '../../common/components/lists/list-item-snippet.component';
import { removeJob } from '../store/jobs.actions';
import { Package } from '../../packages/package.models';

function isJobRunning(item: Job): boolean {
  if (!item || !item.status) {
    return false;
  }
  return [JobStatus.stopping, JobStatus.pending_stoppage, JobStatus.running].includes(item.status);
}

@Component({
  selector: 'job-list-item',
  template: `
    <div class="generic-list-item slider-animation jobs" [ngClass]="{ 'closed-animation': item.isRemoved }">
      <div
        class="job-list item {{ item.status }}"
        [ngClass]="{
          selected: item.selected,
          'pending-removal': item.pending_removal,
          inactive: item.status == 'archived'
        }"
        id="job-{{ item.id }}"
      >
        <div class="job">
          <svg class="icon icons-master-expanded_icon-navmain-job">
            <use href="#img-sprite_icons-master-expanded_icon-navmain-job"></use>
          </svg>
          <span class="job-item" ui-sref-active="active"
            ><a (click)="openJobDetailsModal(item)">{{ item.id }}</a></span
          >
          <span class="job-date" *ngIf="item.creator.type == 'User'">
            <span
              class="date-tooltip"
              [matTooltip]="item.created_at | xpDateUTC"
              matTooltipPosition="above"
              matTooltipClass="above"
              >{{ item.created_at | xpPrettyDate }}</span
            >
            <br />by
            <a (click)="goToCreator(item.creator, $event)" class="owner">{{
              item.creator.display_name | xpLengthCheck: 22
            }}</a>
          </span>
          <span class="job-date" *ngIf="item.creator.type == 'Schedule'">
            <span
              class="date-tooltip"
              [matTooltip]="item.created_at | xpDateUTC"
              matTooltipPosition="above"
              matTooltipClass="above"
              >{{ item.created_at | xpPrettyDate }}</span
            >
            <br />by
            <a (click)="goToCreator(item.creator)" class="owner">
              {{ item.creator.display_name | xpLengthCheck: 22 }}</a
            >
          </span>
        </div>
        <div class="package">
          <div class="package-icon">
            <svg
              *ngIf="item.package.flow_type === 'workflow'"
              class="icon icons-master-expanded_icon-navmain-packages icon-workflow"
            >
              <use href="#img-sprite_icon-icons-master-expanded_icon-navmain-workflow"></use>
            </svg>
            <svg *ngIf="item.package.flow_type === 'dataflow'" class="icon icons-master-expanded_icon-navmain-packages">
              <use href="#img-sprite_icons-master-expanded_icon-navmain-packages"></use>
            </svg>
          </div>
          <div class="package-name">
            <a (click)="goToPackage(item.package, $event)">{{ item.package.name | xpLengthCheck: 42 }}</a>
          </div>
        </div>
        <div class="cluster" *ngIf="!item.cluster">
          <span>No cluster</span>
        </div>
        <div class="cluster" *ngIf="item.cluster">
          <strong
            ><span>{{ item.cluster.name | xpLengthCheck: 32 }}</span></strong
          >
          <p>
            <span>{{ 'cluster-view.labels.statuses.' + item.cluster.status | translate }}</span
            ><span class="left-separator">
              <span *ngIf="item.cluster.type == 'production'" [ngClass]="item.cluster.type">{{
                'job.generic-list.item.labels.nodes' | translate: { count: item.cluster.nodes }
              }}</span>
              <span *ngIf="item.cluster.type == 'sandbox'" [ngClass]="item.cluster.type">{{
                'cluster.generic-list.item.labels.sandbox' | translate
              }}</span>
              <span *ngIf="item.cluster.type == 'managed'" [ngClass]="item.cluster.type">{{
                'cluster.generic-list.item.labels.managed' | translate
              }}</span>
            </span>
          </p>
        </div>
        <div class="runtime">
          <p *ngIf="item.started_at">{{ runTimeInSeconds | xpFormatTime }}</p>
          <p *ngIf="!item.started_at">---</p>
          <p class="small" *ngIf="item.started_at">
            {{ 'job.generic-list.item.labels.started' | translate }}
            <span
              class="date-tooltip"
              [matTooltip]="item.started_at | xpDateUTC"
              matTooltipPosition="above"
              matTooltipClass="above"
            >
              {{ item.started_at | xpCalendar }}
            </span>
          </p>
          <p class="small" *ngIf="item.completed_at">
            {{ 'job.generic-list.item.labels.completed' | translate }}
            <span
              class="date-tooltip"
              [matTooltip]="item.completed_at | xpDateUTC"
              matTooltipPosition="above"
              matTooltipClass="above"
            >
              {{ item.completed_at | xpCalendar }}
            </span>
          </p>
        </div>
        <div class="status">
          <div [ngClass]="item.status" class="status-box" *ngIf="item.package.flow_type === 'dataflow'">
            <div class="job-status-percent">
              <xp-number-tracker
                [isFinished]="item.status === jobStatus.completed"
                [end]="statusPercent"
              ></xp-number-tracker>
              <span>%</span>
            </div>
            <div class="job-status-name">{{ 'job.generic-list.item.labels.' + item.status | translate }}</div>
          </div>
          <div class="status-label " [ngClass]="item.status" *ngIf="item.package.flow_type === 'workflow'">
            {{ 'job.generic-list.item.labels.' + item.status | translate }}
          </div>
          <button class="status-button" [ngClass]="item.status" (click)="openJobDetailsModal(item)">
            {{ 'job.generic-list.item.labels.view_details' | translate }}
          </button>
        </div>
        <div
          [hidden]="hideDropDown"
          class="dropdown xp-dropdown-full"
          [matMenuTriggerFor]="dropdown"
          *ngxPermissionsOnly="'updateJob'"
          [ngClass]="{
            'dropdown-disable': !(
              item.status == jobStatus.running ||
              item.status == jobStatus.pending ||
              item.status == jobStatus.idle
            )
          }"
        >
          <i class="fa fa-ellipsis-v"></i>
        </div>
        <mat-menu #dropdown="matMenu" class="full-dropdown-menu">
          <li mat-menu-item (click)="stopJob(item)">
            <span class="text-danger">{{ 'job.generic-list.item.actions.stop' | translate }}</span>
          </li>
        </mat-menu>
        <div
          class="status-progress-bar"
          [style]="'width:' + statusPercent + '%;'"
          [ngClass]="{
            'progress-bar-primary': item.status == jobStatus.running,
            'progress-bar-danger': item.status === jobStatus.failed,
            'progress-bar-success': item.status === jobStatus.completed,
            'progress-bar-warning': [
              jobStatus.idle,
              jobStatus.pending,
              jobStatus.stopped,
              jobStatus.stopping,
              jobStatus.pending_stoppage
            ].includes(item.status)
          }"
        ></div>
      </div>
    </div>
  `,
})
export class JobListItemComponent implements OnChanges, OnDestroy {
  @Input() item: Job;
  @Input() hideDropDown = false;
  @Output() openJobDetails = new EventEmitter<Job>();

  jobStatus = JobStatus;
  previousProgress = 0;
  previousStatus = '';
  statusPercent = 0;
  counterIntervalRef: any;
  runTimeInSeconds: number;

  constructor(
    private permissionsService: PermissionsService,
    private translate: TranslateService,
    private notify: NotifyService,
    private confirmationDialog: ConfirmationDialogService,
    private store: Store<AppState>,
    @Inject('$rootScope') private $rootScope: any,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.item) {
      this.updateRuntime(changes.item.currentValue);
    }
  }

  updateRuntime(item: Job) {
    if (!item) {
      this.updateProgress(0);
      this.previousProgress = 0;
      this.previousStatus = '';
      this.runTimeInSeconds = 0;
      return;
    }
    if (isJobRunning(item)) {
      this.stopCounter();
      this.runCounter();
      if (item.progress > this.previousProgress || item.status !== this.previousStatus) {
        this.updateProgress(item.progress);
        this.previousProgress = item.progress;
        this.previousStatus = item.status;
      }
    } else {
      this.stopCounter();
      this.updateProgress(item.progress);
      this.previousProgress = item.progress;
      this.previousStatus = item.status;
      this.runTimeInSeconds = this.item.runtime_in_seconds;
    }
  }

  runCounter() {
    this.counterIntervalRef = setInterval(() => {
      const startTime = this.item.started_at ? moment(this.item.started_at) : moment();
      const endTime = this.item.completed_at ? moment(this.item.completed_at) : moment();
      this.runTimeInSeconds = endTime.diff(startTime, 'seconds');
    }, 1000);
  }

  stopCounter() {
    clearInterval(this.counterIntervalRef);
  }

  updateProgress(from: number = 0) {
    this.statusPercent = from * 100;
  }

  hasPermission(permissionName) {
    return this.permissionsService.hasPermission(permissionName);
  }

  openJobDetailsModal(item: Job): void {
    this.openJobDetails.emit(item);
  }

  goToCreator(creator: Creator, event: any = {}): void {
    const openInNewTab = event.metaKey || event.ctrlKey || event.isMiddleClick;
    if (creator.type === 'Schedule') {
      this.$rootScope.stateGo('app.account.schedules.item.edit', creator, openInNewTab);
    }

    if (creator.type === 'User') {
      this.$rootScope.stateGo('app.account.settings.account.member', creator, openInNewTab);
    }
  }

  goToPackage(packageItem: Package, event: any = {}): void {
    const openInNewTab = event.metaKey || event.ctrlKey || event.isMiddleClick;
    this.$rootScope.stateGo('app.account.packages.item.edit', packageItem, openInNewTab);
  }

  stopJob(job: Job) {
    const dialogRef = this.confirmationDialog.openDialog({
      title: this.translate.instant('job.generic-list.item.modals.confirm.title'),
      hint: this.translate.instant('job.generic-list.item.modals.confirm.hint'),
      yes: this.translate.instant('job.generic-list.item.modals.confirm.yes'),
      no: this.translate.instant('job.generic-list.item.modals.confirm.no'),
      item: job,
      itemType: ItemType.job,
    });
    dialogRef.afterClosed().subscribe((isConfirmed) => {
      if (isConfirmed) {
        this.store.dispatch(removeJob({ jobId: job.id }));
      }
    });
  }

  ngOnDestroy() {
    this.stopCounter();
  }
}
